<template>
  <section
    class="position-relative overflow-hidden svm-section bg-dark-3"
    :style="bgImageStyle">
    <!-- video background desktop-->
    <iframe
      title="Video"
      class="d-none d-md-block position-absolute w-100 border-0 svm-video-iframe"
      :src="bgVideoUrl"></iframe>

    <!-- foreground -->
    <div
      class="position-relative h-100 d-flex align-items-center justify-content-center"
      :style="bgShadow">
      <div class="container">
        <div class="row">
          <div class="col-12 text-center">
            <h2 class="mb-space-xs mb-lg-space-sm" :class="titleClass">
              {{ title }}
            </h2>
            <button
              aria-label="watch video"
              class="modal-play-button"
              :class="modalTextClass"
              @click="handleModalOpen($event)">
              <font-awesome-icon v-if="displayPlayButton" :icon="faPlay" />
              <template v-else>{{ modalText }}</template>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- video modal -->
    <rds-modal
      :visible="showModal"
      centered
      exterior-close-btn
      size="xl"
      body-class="p-0"
      @hidden="handleModalClose($event)"
      bg-variant="transparent">
      <div class="ratio ratio-16x9">
        <iframe
          title="Video"
          class="w-100 h-100"
          :src="videoSource"
          ref="video"></iframe>
      </div>
    </rds-modal>
  </section>
</template>

<script setup lang="ts">
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { RdsModal } from "@rds-vue-ui/rds-modal";
import { computed, ref } from "vue";

interface Props {
  videoSource: string;
  bgImageSource: string;
  bgShadowOpacity?: number;
  title: string;
  titleSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
  displayPlayButton?: boolean;
  modalText?: string;
  modalTextSize?: "small" | "medium" | "large" | "xl";
  modalTextVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  bgShadowOpacity: 0.8,
  titleSize: "medium",
  titleVariant: "white",
  modalText: "Watch video",
  modalTextSize: "xl",
  modalTextVariant: "secondary",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the modal is closed.
   */
  (e: "modalClose", event: Event): void;
  /**
   *  Emitted when the modal is opened.
   */
  (e: "modalOpen", event: Event): void;
}>();

const showModal = ref(false);
const video = ref<HTMLIFrameElement | null>();

const bgImageStyle = computed(() => {
  return {
    "background-image": "url(" + props.bgImageSource + ")",
  };
});

const videoId = computed(() => {
  return props.videoSource.split("/").slice(-1);
});

const bgVideoUrl = computed(() => {
  return (
    props.videoSource +
    "?autoplay=1&loop=1&mute=1&controls=0&cc_load_policy=0&disablekb=1&iv_load_policy=3modestbranding=1&rel=0&playlist=" +
    videoId.value
  );
});

const bgShadow = computed(() => {
  return {
    "background-color": "rgba(0,0,0, " + props.bgShadowOpacity + ")",
  };
});

const titleClass = computed(() => {
  return `h2-${props.titleSize} text-${props.titleVariant}`;
});

const modalTextClass = computed(() => {
  return `fs-${props.modalTextSize} text-${props.modalTextVariant}`;
});

const handleModalOpen = (event: Event) => {
  showModal.value = true;
  emits("modalOpen", event);
};

const handleModalClose = (event: Event) => {
  video.value!.src = video.value!.src;
  showModal.value = false;
  emits("modalClose", event);
};
</script>

<style lang="scss" scoped>
.svm-video-iframe {
  aspect-ratio: 16/9;
}

.svm-section {
  height: 500px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.svm-modal-close-btn {
  right: -3%;
}

.modal-play-button {
  border: none;
  background: transparent;
  padding: 0;
  height: max-content;
  & * {
    box-sizing: border-box !important;
  }
  svg {
    border: 1px var(--rds-light-4, #d0d0d0) solid;
    border-radius: 50%;
    color: var(--rds-dark-3, #191919);
    background: var(--rds-white, #ffffff);
    height: 8rem;
    width: 8rem;
    padding: 2.75rem;
    opacity: 0.7;
    transition:
      transform 400ms,
      opacity 400ms;
    &:hover {
      opacity: 1;
      transform: scale(110%, 110%);
    }
  }

  &:focus {
    outline: var(--rds-white, #ffffff) solid 2px;
    outline-offset: 2px;
  }
}

@media screen and (max-width: 1200px) {
  .svm-modal-close-btn {
    right: -8%;
  }
}

@media screen and (max-width: 992px) {
  .modal-play-button svg {
    height: 7rem;
    width: 7rem;
    padding: 2.5rem;
  }
}
/* Medium devices (tablets and smaller, 768px) */
@media screen and (max-width: 768px) {
  .svm-section {
    height: 400px;
  }
  .svm-modal-close-btn {
    right: -10%;
  }
  .modal-play-button svg {
    height: 6rem;
    width: 6rem;
    padding: 2rem;
  }
}
/* Small devices (mobiles, 576px) */
@media screen and (max-width: 575px) {
  .svm-section {
    height: 300px;
  }
  .svm-modal-close-btn {
    right: 0;
    top: -15%;
  }
  .modal-play-button svg {
    height: 4rem;
    width: 4rem;
    padding: 1.25rem;
  }
}
</style>
